import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPaymentHistory, getLoan, formatCurrency } from '../api';
// loan.monthly_payment is principal and interest alone. What the buyer owes each
// month is that plus the escrow frozen on the loan.
import { totalMonthlyPayment, monthlyPaymentSplitText } from '../utils/monthlyPayment';
import { useConfig } from '../context/ConfigContext';

function PaymentHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCompanyLegalName } = useConfig();
  
  const [payments, setPayments] = useState([]);
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const loadData = useCallback(async () => {
    try {
      const [paymentsResponse, loanResponse] = await Promise.all([
        getPaymentHistory(id),
        getLoan(id)
      ]);
      
      setPayments(paymentsResponse.data);
      setLoan(loanResponse.data);
    } catch (err) {
      setError('Failed to load payment history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading payment history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-history">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const totalPaid = payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);

  return (
    <div className="payment-history">
      <div className="payment-history-button-container">
        <button onClick={() => navigate(`/loans/${id}`)} className="btn btn-secondary">
          ← Back to Loan Details
        </button>
        <button 
          onClick={() => {
            const printWindow = window.open('', '_blank', 'width=800,height=900,left=200,top=50');
            printWindow.document.write(`
              <html>
                <head>
                  <title>Payment History - ${loan?.property_title || 'Loan'}</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { color: #2c5f2d; }
                    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                    th, td { padding: 10px; border: 1px solid #ddd; text-align: left; }
                    th { background-color: #f0f8f0; font-weight: bold; }
                    .summary { background: #f0f8f0; padding: 15px; border-radius: 8px; margin: 20px 0; }
                  </style>
                </head>
                <body>
                  <h1>${getCompanyLegalName()}</h1>
                  <h2>Payment History</h2>
                  <h3>${loan?.property_title || ''}</h3>
                  <div class="summary">
                    <strong>Total Paid:</strong> $${formatCurrency(totalPaid)}<br/>
                    <strong>Remaining Balance:</strong> $${formatCurrency(loan?.balance_remaining || 0)}<br/>
                    <strong>Monthly Payment:</strong> $${formatCurrency(totalMonthlyPayment(loan))}
                    ${monthlyPaymentSplitText(loan)
                      ? `<br/><span style="font-size: 13px; color: #666;">${monthlyPaymentSplitText(loan)}</span>`
                      : ''}
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${payments.map(payment => `
                        <tr>
                          <td>${new Date(payment.payment_date).toLocaleDateString()}</td>
                          <td>$${formatCurrency(payment.amount)}</td>
                          <td>${payment.payment_type === 'down_payment' ? 'Down Payment' : payment.payment_type === 'processing_fee' ? 'Processing Fee' : 'Monthly Payment'}</td>
                          <td>${payment.status}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                  <p style="margin-top: 40px; font-size: 12px; color: #666;">Generated: ${new Date().toLocaleDateString()}</p>
                </body>
              </html>
            `);
            printWindow.document.close();
            printWindow.print();
          }}
          className="btn btn-primary"
        >
          🖨️ Print Payment History
        </button>
      </div>
      
      <h1>Payment History</h1>
      
      {loan && (
        <div className="payment-summary">
          <h2>{loan.property_title}</h2>
          <div className="summary-stats">
            <div>
              <strong>Total Paid:</strong> ${formatCurrency(totalPaid)}
            </div>
            <div>
              <strong>Remaining Balance:</strong> ${formatCurrency(loan.balance_remaining)}
            </div>
            <div>
              {/* P&I plus the escrow frozen on this loan -- what the buyer
                  actually pays each month. loan.monthly_payment is P&I alone. */}
              <strong>Monthly Payment:</strong> ${formatCurrency(totalMonthlyPayment(loan))}
              {monthlyPaymentSplitText(loan) && (
                <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
                  {monthlyPaymentSplitText(loan)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {payments.length === 0 ? (
        <div className="empty-state">
          <p>No payments recorded yet.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="payments-table desktop-only">
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>Date</th>
                  <th style={{ minWidth: '100px' }}>Type</th>
                  <th style={{ minWidth: '80px', textAlign: 'right' }}>Amount</th>
                  <th style={{ minWidth: '75px', textAlign: 'right' }}>Principal</th>
                  <th style={{ minWidth: '75px', textAlign: 'right' }}>Interest</th>
                  <th style={{ minWidth: '65px', textAlign: 'right' }}>Tax</th>
                  <th style={{ minWidth: '65px', textAlign: 'right' }}>HOA</th>
                  {/* Short of the period, or catching up. One column, because
                      no payment is ever both. */}
                  <th style={{ minWidth: '75px', textAlign: 'right' }}>Arrears</th>
                  <th style={{ minWidth: '80px' }}>Method</th>
                  <th style={{ minWidth: '80px', textAlign: 'center' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="payment-table-date">
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </td>
                    <td className="payment-table-type">
                      {payment.payment_type === 'down_payment' ? 'Down Payment' : payment.payment_type === 'processing_fee' ? 'Processing Fee' : 'Monthly Payment'}
                    </td>
                    <td className="payment-table-amount">
                      ${formatCurrency(payment.amount)}
                    </td>
                    <td className="payment-table-principal">
                      {payment.principal_amount ? `$${formatCurrency(payment.principal_amount)}` : '—'}
                    </td>
                    <td className="payment-table-interest">
                      {payment.interest_amount ? `$${formatCurrency(payment.interest_amount)}` : '—'}
                    </td>
                    <td className="payment-table-tax">
                      {payment.tax_amount ? `$${formatCurrency(payment.tax_amount)}` : '—'}
                    </td>
                    <td className="payment-table-hoa">
                      {payment.hoa_amount ? `$${formatCurrency(payment.hoa_amount)}` : '—'}
                    </td>
                    {/* What this payment fell short by, or paid down off an
                        earlier shortfall. Rendered only when non-zero, so a
                        history of full payments looks exactly as it did. */}
                    <td style={{ textAlign: 'right', fontSize: '0.85rem' }}>
                      {parseFloat(payment.shortfall_amount) > 0 && (
                        <div style={{ color: 'var(--color-error-text)' }}>
                          -${formatCurrency(payment.shortfall_amount)}
                        </div>
                      )}
                      {parseFloat(payment.arrears_cleared) > 0 && (
                        <div style={{ color: 'var(--color-success-text)' }}>
                          +${formatCurrency(payment.arrears_cleared)}
                        </div>
                      )}
                      {!(parseFloat(payment.shortfall_amount) > 0)
                        && !(parseFloat(payment.arrears_cleared) > 0) && '—'}
                    </td>
                    <td className="payment-table-method">
                      {payment.payment_method || 'Square'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          const printWindow = window.open('', '_blank', 'width=600,height=700,left=300,top=100');
                          printWindow.document.write(`
                            <html>
                              <head>
                                <title>Payment Receipt #${payment.id}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; padding: 30px; }
                                  h1 { color: #2c5f2d; margin-bottom: 5px; }
                                  h2 { color: #666; margin-top: 0; }
                                  table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                                  td { padding: 10px; border-bottom: 1px solid #ddd; }
                                  .label { font-weight: bold; width: 40%; }
                                  .value { text-align: right; }
                                  .total { background: #f0f8f0; font-size: 18px; font-weight: bold; }
                                </style>
                              </head>
                              <body>
                                <h1>${getCompanyLegalName()}</h1>
                                <h2>Payment Receipt</h2>
                                <p><strong>Receipt #${payment.id}</strong></p>
                                <p><strong>Property:</strong> ${loan?.property_title || ''}</p>
                                <table>
                                  <tr><td class="label">Payment Date:</td><td class="value">${new Date(payment.payment_date).toLocaleDateString()}</td></tr>
                                  <tr><td class="label">Payment Type:</td><td class="value">${payment.payment_type === 'down_payment' ? 'Down Payment' : payment.payment_type === 'processing_fee' ? 'Processing Fee' : 'Monthly Payment'}</td></tr>
                                  ${payment.principal_amount ? `<tr><td class="label">Principal:</td><td class="value">$${formatCurrency(payment.principal_amount)}</td></tr>` : ''}
                                  ${payment.interest_amount ? `<tr><td class="label">Interest:</td><td class="value">$${formatCurrency(payment.interest_amount)}</td></tr>` : ''}
                                  ${payment.tax_amount ? `<tr><td class="label">Property Tax:</td><td class="value">$${formatCurrency(payment.tax_amount)}</td></tr>` : ''}
                                  ${payment.hoa_amount ? `<tr><td class="label">HOA Fee:</td><td class="value">$${formatCurrency(payment.hoa_amount)}</td></tr>` : ''}
                                  <tr class="total"><td class="label">Total Amount:</td><td class="value">$${formatCurrency(payment.amount)}</td></tr>
                                  <tr><td class="label">Payment Method:</td><td class="value">${payment.payment_method || 'Square'}</td></tr>
                                  <tr><td class="label">Status:</td><td class="value">${payment.status}</td></tr>
                                </table>
                                <p style="margin-top: 40px; font-size: 12px; color: #666;">
                                  Thank you for your payment!<br/>
                                  Generated: ${new Date().toLocaleDateString()}
                                </p>
                              </body>
                            </html>
                          `);
                          printWindow.document.close();
                          printWindow.print();
                        }}
                        className="btn btn-small payment-table-receipt-btn"
                      >
                        🖨️ Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div className="payments-total">
              <strong>Total Payments:</strong> ${formatCurrency(totalPaid)}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="payments-cards mobile-only">
            {payments.map((payment) => (
              <div key={payment.id} className="payment-card-mobile">
                <div className="payment-card-header">
                  <div className="payment-card-date-section">
                    <strong>{new Date(payment.payment_date).toLocaleDateString()}</strong>
                    <br />
                    <span>
                      {payment.payment_type === 'down_payment' ? 'Down Payment' : payment.payment_type === 'processing_fee' ? 'Processing Fee' : 'Monthly Payment'}
                    </span>
                  </div>
                  <div className="payment-card-amount-section">
                    <div className="payment-card-amount">
                      ${formatCurrency(payment.amount)}
                    </div>
                    <span className={`status-badge status-${payment.status}`}>
                      {payment.status === 'completed' ? 'Completed' : payment.status}
                    </span>
                  </div>
                </div>
                
                <div className="payment-card-breakdown">
                  <div>
                    <strong>Principal:</strong>
                    <br />
                    <span className="payment-card-breakdown-value-principal">
                      {payment.principal_amount ? `$${formatCurrency(payment.principal_amount)}` : '—'}
                    </span>
                  </div>
                  <div>
                    <strong>Interest:</strong>
                    <br />
                    <span className="payment-card-breakdown-value-interest">
                      {payment.interest_amount ? `$${formatCurrency(payment.interest_amount)}` : '—'}
                    </span>
                  </div>
                  <div>
                    <strong>Tax:</strong>
                    <br />
                    <span className="payment-card-breakdown-value-tax">
                      {payment.tax_amount ? `$${formatCurrency(payment.tax_amount)}` : '—'}
                    </span>
                  </div>
                  <div>
                    <strong>HOA:</strong>
                    <br />
                    <span className="payment-card-breakdown-value-hoa">
                      {payment.hoa_amount ? `$${formatCurrency(payment.hoa_amount)}` : '—'}
                    </span>
                  </div>
                  {/* Only when there is something to say. An extra grid cell on
                      every card would cost a row of vertical space on a phone
                      to print a dash. */}
                  {parseFloat(payment.shortfall_amount) > 0 && (
                    <div>
                      <strong>Shortfall:</strong>
                      <br />
                      <span style={{ color: 'var(--color-error-text)' }}>
                        ${formatCurrency(payment.shortfall_amount)}
                      </span>
                    </div>
                  )}
                  {parseFloat(payment.arrears_cleared) > 0 && (
                    <div>
                      <strong>Arrears Paid:</strong>
                      <br />
                      <span style={{ color: 'var(--color-success-text)' }}>
                        ${formatCurrency(payment.arrears_cleared)}
                      </span>
                    </div>
                  )}
                  <div>
                    <strong>Method:</strong>
                    <br />
                    <span className="payment-card-method">
                      {payment.payment_method || 'Square'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    const printWindow = window.open('', '_blank', 'width=600,height=700,left=300,top=100');
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Payment Receipt #${payment.id}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 30px; }
                            h1 { color: #2c5f2d; margin-bottom: 5px; }
                            h2 { color: #666; margin-top: 0; }
                            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                            td { padding: 10px; border-bottom: 1px solid #ddd; }
                            .label { font-weight: bold; width: 40%; }
                            .value { text-align: right; }
                            .total { background: #f0f8f0; font-size: 18px; font-weight: bold; }
                          </style>
                        </head>
                        <body>
                          <h1>${getCompanyLegalName()}</h1>
                          <h2>Payment Receipt</h2>
                          <p><strong>Receipt #${payment.id}</strong></p>
                          <p><strong>Property:</strong> ${loan?.property_title || ''}</p>
                          <table>
                            <tr><td class="label">Payment Date:</td><td class="value">${new Date(payment.payment_date).toLocaleDateString()}</td></tr>
                            <tr><td class="label">Payment Type:</td><td class="value">${payment.payment_type === 'down_payment' ? 'Down Payment' : payment.payment_type === 'processing_fee' ? 'Processing Fee' : 'Monthly Payment'}</td></tr>
                            ${payment.principal_amount ? `<tr><td class="label">Principal:</td><td class="value">$${formatCurrency(payment.principal_amount)}</td></tr>` : ''}
                            ${payment.interest_amount ? `<tr><td class="label">Interest:</td><td class="value">$${formatCurrency(payment.interest_amount)}</td></tr>` : ''}
                            ${payment.tax_amount ? `<tr><td class="label">Property Tax:</td><td class="value">$${formatCurrency(payment.tax_amount)}</td></tr>` : ''}
                            ${payment.hoa_amount ? `<tr><td class="label">HOA Fee:</td><td class="value">$${formatCurrency(payment.hoa_amount)}</td></tr>` : ''}
                            <tr class="total"><td class="label">Total Amount:</td><td class="value">$${formatCurrency(payment.amount)}</td></tr>
                            <tr><td class="label">Payment Method:</td><td class="value">${payment.payment_method || 'Square'}</td></tr>
                            <tr><td class="label">Status:</td><td class="value">${payment.status}</td></tr>
                          </table>
                          <p style="margin-top: 40px; font-size: 12px; color: #666;">
                            Thank you for your payment!<br/>
                            Generated: ${new Date().toLocaleDateString()}
                          </p>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }}
                  className="btn btn-small btn-secondary payment-card-receipt-btn"
                >
                  🖨️ Print Receipt
                </button>
              </div>
            ))}

            <div className="payments-total">
              <strong>Total Payments:</strong> ${formatCurrency(totalPaid)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PaymentHistory;